import { useMemo, useState } from 'react';
import { Autocomplete, Box, IconButton, TextField, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { useProjects } from '../../api/hooks/useProjects';
import { ApiError } from '../../api/client';
import { useActiveProject } from '../../context/ActiveProjectContext';
import { ProjectFormDialog } from '../../pages/projects/components/ProjectFormDialog';

interface ProjectListItem {
  id: string;
  name: string;
  project_no: string | null;
}

interface OptionType {
  id: string;
  label: string;
}

export function ProjectSwitcher() {
  const { t } = useTranslation(['projects', 'common']);
  const { data, error } = useProjects({ limit: 100 });
  const { activeProjectId, switchProject } = useActiveProject();
  const [createOpen, setCreateOpen] = useState(false);

  const projects = (data as unknown as { data?: ProjectListItem[] } | undefined)?.data ?? [];

  const options = useMemo<OptionType[]>(
    () => projects.map((p) => ({
      id: p.id,
      label: p.project_no ? `${p.project_no} – ${p.name}` : p.name,
    })),
    [projects],
  );

  const value = options.find((o) => o.id === activeProjectId) ?? null;

  // 403 = fehlendes Projekt-Leserecht im aktuellen Mandanten. In der Kopfleiste
  // unaufdringlich als Sperre zeigen (deaktiviert + Hinweis) statt als leere Liste.
  const forbidden = error instanceof ApiError && error.status === 403;
  const noAccessLabel = t('projects:switcher.noAccess', 'Kein Zugriff auf Projekte in diesem Mandanten');

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title={forbidden ? noAccessLabel : ''}>
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <Autocomplete
            size="small"
            options={options}
            value={value}
            disabled={forbidden}
            onChange={(_e, next) => { if (next) switchProject(next.id); }}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            sx={{ width: 320 }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={forbidden ? noAccessLabel : t('projects:switcher.placeholder', 'Projekt auswählen…')}
                variant="outlined"
              />
            )}
          />
        </Box>
      </Tooltip>
      <Tooltip title={t('projects:project.create', 'Neues Projekt')}>
        <IconButton
          color="primary"
          onClick={() => setCreateOpen(true)}
          size="small"
          sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <ProjectFormDialog open={createOpen} mode="create" onClose={() => setCreateOpen(false)} />
    </Box>
  );
}
